import React from "react";
import {
  CURRENCY_CONFIG,
  CURRENCY_LOCALES,
  DEFAULT_CURRENCY_LOCALE,
} from "../config/currency";

// Import all Dhiram icons
import { ReactComponent as DhiramIcon } from "../assets/svg/DhiramIcon.svg";
import { ReactComponent as SmallDhiramIcon } from "../assets/svg/SmallDhiramIcon.svg";
import { ReactComponent as LargeDhiramIcon } from "../assets/svg/LargeDhiramIcon.svg";
import { ReactComponent as BlackSmallDhiramIcon } from "../assets/svg/BlackSmallDhiramIcon.svg";
import { ReactComponent as MwebDhiramIcon } from "../assets/svg/MwebDhiramIcon.svg";
import { ReactComponent as MwebBlackDhiramIcon } from "../assets/svg/MwebBlackDhiramIcon.svg";
import { getCurrentLocale } from "./Setting";

/**
 * Get current currency locale from environment variable or default
 * @returns {string} Currency locale (AED or EGP)
 */
export const getCurrentCurrencyLocale = () => {
  // Get locale from environment variable (replaces localStorage concept)
  const envLocale = getCurrentLocale();

  if (envLocale === "en-EG") {
    console.log("💰 Currency from environment (EGP branch):", "EGP");
    return "EGP";
  } else if (envLocale === "en-AE") {
    console.log("💰 Currency from environment (AED branch):", "AED");
    return "AED";
  }

  return DEFAULT_CURRENCY_LOCALE;
};

/**
 * Set currency locale in localStorage (for backward compatibility)
 * Note: In EGP branch, currency is controlled by environment variable
 * @param {string} locale - Currency locale (AED or EGP)
 */
export const setCurrencyLocale = (locale) => {
  console.log("💰 Attempting to set currency locale to:", locale);
  throw new Error(
    "Setting currency locale is disabled. Currency is determined by environment variable."
  );
  // Check if we're in EGP branch - if so, don't allow changes
  const envLocale = process.env.REACT_APP_LOCALE;
  if (envLocale === "en-EG") {
    console.log("💰 Currency change blocked - EGP branch is locked to EGP");
    return false;
  }

  try {
    if (Object.values(CURRENCY_LOCALES).includes(locale)) {
      localStorage.setItem("currencyLocale", locale);
      // Trigger a custom event to notify components of locale change
      window.dispatchEvent(
        new CustomEvent("currencyLocaleChanged", { detail: locale })
      );
      return true;
    }
  } catch (error) {
    console.warn("Error setting currency locale:", error);
  }
  return false;
};

/**
 * Get currency configuration for current locale
 * @param {string} locale - Optional locale override
 * @returns {object} Currency configuration
 */
export const getCurrentCurrencyConfig = (locale = null) => {
  const currentLocale = locale || getCurrentCurrencyLocale();
  return (
    CURRENCY_CONFIG[currentLocale] || CURRENCY_CONFIG[DEFAULT_CURRENCY_LOCALE]
  );
};

// No conversion functions needed - amounts stay the same

/**
 * Format number with currency configuration
 * @param {number} amount - Amount to format
 * @param {string} locale - Optional locale override
 * @returns {string} Formatted amount string
 */
export const formatCurrencyAmount = (amount, locale = null) => {
  if (amount === null || amount === undefined) {
    return "-";
  }

  if (isNaN(amount)) {
    return "";
  }

  const config = getCurrentCurrencyConfig(locale);
  const number = parseFloat(amount).toFixed(config.decimalPlaces);
  const [integerPart, decimalPart] = number.split(".");

  // Add thousands separator
  const formattedInteger = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    config.thousandsSeparator
  );
  const formattedAmount =
    formattedInteger + config.decimalSeparator + decimalPart;

  return formattedAmount;
};

/**
 * Get appropriate currency icon component based on icon type and locale
 * @param {string} iconType - Type of icon ('default', 'small', 'large', 'black-small', 'mweb', 'mweb-black')
 * @param {string} locale - Optional locale override
 * @returns {React.Component|string} Icon component or text
 */
export const getCurrencyIcon = (iconType = "default", locale = null) => {
  const config = getCurrentCurrencyConfig(locale);

  // If EGP locale and showIcon is false, return text
  if (!config.showIcon) {
    return config.displayName;
  }

  // For AED, return appropriate icon
  switch (iconType) {
    case "small":
      return SmallDhiramIcon;
    case "large":
      return LargeDhiramIcon;
    case "black-small":
      return BlackSmallDhiramIcon;
    case "mweb":
      return MwebDhiramIcon;
    case "mweb-black":
      return MwebBlackDhiramIcon;
    case "default":
    default:
      return DhiramIcon;
  }
};

/**
 * Render currency icon or text based on configuration
 * @param {string} iconType - Type of icon
 * @param {object} props - Props to pass to icon component
 * @param {string} locale - Optional locale override
 * @returns {React.Element} Rendered icon or text
 */
export const renderCurrencyIcon = (
  iconType = "default",
  props = {},
  locale = null
) => {
  const config = getCurrentCurrencyConfig(locale);

  // If EGP locale and showIcon is false, return text
  if (!config.showIcon) {
    return <span {...props}>{config.displayName}</span>;
  }

  // For AED, return appropriate icon component
  const IconComponent = getCurrencyIcon(iconType, locale);
  return <IconComponent {...props} />;
};

/**
 * Format amount with currency symbol/icon (no conversion, just display)
 * @param {number} amount - Amount to format
 * @param {string} iconType - Type of icon to use
 * @param {object} iconProps - Props to pass to icon
 * @param {string} locale - Optional locale override
 * @returns {React.Element} Formatted amount with currency
 */
export const formatAmountWithCurrency = (
  amount,
  iconType = "default",
  iconProps = {},
  locale = null
) => {
  const config = getCurrentCurrencyConfig(locale);
  const formattedAmount = formatCurrencyAmount(amount, locale);

  if (formattedAmount === "-" || formattedAmount === "") {
    return formattedAmount;
  }

  const currencyIcon = renderCurrencyIcon(iconType, iconProps, locale);

  const containerStyle = {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "3px",
    ...iconProps.containerStyle,
  };

  return (
    <span style={containerStyle}>
      {config.symbolPosition === "before" && currencyIcon}
      {formattedAmount}
      {config.symbolPosition === "after" && currencyIcon}
    </span>
  );
};

/**
 * Get formatted amount without currency symbol (for backward compatibility)
 * @param {number} amount - Amount to format
 * @param {string} locale - Optional locale override
 * @returns {string} Formatted amount without currency
 */
export const getFormattedAmountWithoutCurrency = (amount, locale = null) => {
  return formatCurrencyAmount(amount, locale);
};

// No exchange rate updates needed since we're not converting amounts

/**
 * Hook to listen for currency locale changes
 * @param {function} callback - Callback function to call when locale changes
 */
export const useCurrencyLocaleListener = (callback) => {
  React.useEffect(() => {
    const handleLocaleChange = (event) => {
      callback(event.detail);
    };

    window.addEventListener("currencyLocaleChanged", handleLocaleChange);

    return () => {
      window.removeEventListener("currencyLocaleChanged", handleLocaleChange);
    };
  }, [callback]);
};
